// src/hooks/useAuthenticatedRequest.js
import { useSession } from '@clerk/clerk-react';
import { useCallback, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import axiosClient, { setAxiosTokenProvider } from '../api/axiosClient';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const TOKEN_ACTIVATION_EPSILON = 0.1; // seconds; ignore tiny clock drift
const TOKEN_ACTIVATION_BUFFER_SECONDS = 0.75; // wait a little past nbf/iat when needed
const MAX_TOKEN_WAIT_MS = 5000;

const parseEpoch = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
    }
    return null;
};

const ensureTokenReady = async (token, { debug } = {}) => {
    if (!token) return null;
    let claims;
    try {
        claims = jwtDecode(token);
    } catch (err) {
        if (debug) {
            // eslint-disable-next-line no-console
            console.warn('[Auth] Failed to decode Clerk token', err);
        }
        return null;
    }

    const epochs = [parseEpoch(claims?.nbf), parseEpoch(claims?.iat)].filter((v) => typeof v === 'number');
    if (epochs.length) {
        const activation = Math.max(...epochs);
        const nowSeconds = Date.now() / 1000;
        const secondsUntilValid = activation - nowSeconds;
        if (secondsUntilValid > TOKEN_ACTIVATION_EPSILON) {
            const waitMs = Math.min((secondsUntilValid + TOKEN_ACTIVATION_BUFFER_SECONDS) * 1000, MAX_TOKEN_WAIT_MS);
            if (debug) {
                // eslint-disable-next-line no-console
                console.info(`[Auth] Waiting ${Math.round(waitMs)}ms for Clerk token to become valid (nbf/iat=${activation})`);
            }
            await sleep(waitMs);
        }
    }

    return claims;
};

// Toggle auth request logging with env var
const DEBUG_AUTH = Boolean(import.meta.env.VITE_DEBUG_AUTH);
const DEBUG_LOG_JWT = Boolean(import.meta.env.VITE_DEBUG_LOG_JWT) && import.meta.env.DEV;

const API_V1_PREFIX_REGEX = /^\/api\/v1(?=\/|$)/i;
const baseHasApiV1Prefix = (baseURL) => /\/api\/v1\/?$/i.test(String(baseURL || ''));
const normalizeEndpointForBase = (endpoint, baseURL) => {
    if (typeof endpoint !== 'string') return endpoint;
    if (!baseHasApiV1Prefix(baseURL)) return endpoint;
    if (!API_V1_PREFIX_REGEX.test(endpoint)) return endpoint;
    const normalized = endpoint.replace(API_V1_PREFIX_REGEX, '');
    return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

const useAuthenticatedRequest = () => {
    const { session, isLoaded, isSignedIn } = useSession();

    // Sync the Clerk token provider with the axios client whenever the session changes
    useEffect(() => {
        if (isLoaded && isSignedIn && session) {
            setAxiosTokenProvider(() => session.getToken({ template: 'django_backend_template' }));
        } else {
            setAxiosTokenProvider(null);
        }
    }, [isLoaded, isSignedIn, session]);

    const makeAuthenticatedRequest = useCallback(async (endpoint, options = {}) => {
        if (!isLoaded) {
            throw new Error('Authentication state is not loaded yet.');
        }
        if (!isSignedIn || !session) {
            throw new Error('No active session found. Please log in.');
        }

        try {
            // axiosClient's baseURL already includes '/api/v1' (see api/axiosClient.js).
            // useAuthenticatedRequest callers historically pass '/api/v1/...' paths,
            // while direct axiosClient callers pass '/...' paths. Strip the duplicate
            // prefix so both conventions resolve to the same backend URL.
            const initialBaseURL = options.baseURL || axiosClient?.defaults?.baseURL || '';
            const requestConfig = {
                ...options,
                url: normalizeEndpointForBase(endpoint, initialBaseURL)
            };

            const response = await axiosClient(requestConfig);
            return response.data;
        } catch (error) {
            // Surface the backend's `detail` field when present; fall back to a
            // short generic message otherwise. Previously we serialised the
            // full response body into the thrown Error which leaked any
            // backend stack/SQL/internal details into the UI.
            if (error.response) {
                if (DEBUG_AUTH) {
                    const status = error.response.status;
                    const url = error.response?.config?.url || 'unknown-url';
                    // eslint-disable-next-line no-console
                    console.warn(`Auth request failed ${status} @ ${url}`);
                }
                const detail = error.response?.data?.detail;
                const message = typeof detail === 'string' && detail
                    ? detail
                    : `Request failed with status ${error.response.status}`;
                throw new Error(message);
            } else if (error.request) {
                if (DEBUG_AUTH) {
                    // eslint-disable-next-line no-console
                    console.warn('Auth request: no response');
                }
                throw new Error('No response from API');
            } else {
                if (DEBUG_AUTH) {
                    // eslint-disable-next-line no-console
                    console.warn(`Auth request error: ${error.message}`);
                }
                throw new Error(`Error: ${error.message}`);
            }
        }
    }, [session, isLoaded, isSignedIn]);

    return { makeAuthenticatedRequest, isLoaded, isSignedIn };
};

export default useAuthenticatedRequest;
